import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './authApi';

export const cashSessionApi = createApi({
  reducerPath: 'cashSessionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CashSession'],
  endpoints: (builder) => ({

    // POST /cash_sessions — open a new session with opening_balance (+ optional notes, denomination_entries)
    openSession: builder.mutation({
      query: ({ opening_balance, notes, denomination_entries }) => ({
        url: '/cash_sessions',
        method: 'POST',
        body: {
          cash_session: {
            opening_balance,
            notes,
            ...(denomination_entries ? { denomination_entries } : {}),
          },
        },
      }),
      invalidatesTags: [{ type: 'CashSession', id: 'LIST' }],
    }),

    // GET /cash_sessions — paginated list; used on Dashboard mount to detect open/closed session
    listSessions: builder.query({
      query: ({ page = 1, per_page = 10 } = {}) => ({
        url: '/cash_sessions',
        method: 'GET',
        params: { page, per_page },
      }),
      providesTags: [{ type: 'CashSession', id: 'LIST' }],
    }),

    // GET /cash_sessions/:id — full session with live summary
    getSession: builder.query({
      query: (id) => `/cash_sessions/${id}`,
      providesTags: (result, error, id) => [{ type: 'CashSession', id }],
    }),

    // POST /cash_sessions/:id/close — cashier closes session with closing_balance (+ optional denomination_entries)
    closeSession: builder.mutation({
      query: ({ id, closing_balance, denomination_entries }) => ({
        url: `/cash_sessions/${id}/close`,
        method: 'POST',
        body: {
          cash_session: {
            closing_balance,
            ...(denomination_entries ? { denomination_entries } : {}),
          },
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CashSession', id },
        { type: 'CashSession', id: 'LIST' },
      ],
    }),

    // POST /cash_sessions/:cash_session_id/cash_movements — cash in or cash out
    // `reason` is the cashier's comment; cash_movement_reason_id the picked reason.
    // GET /cash_movement_reasons — the org's active Cash In / Cash Out reasons
    // (Other last; comment_required tells the modal when a comment is mandatory).
    getCashMovementReasons: builder.query({
      query: (movementType) => ({ url: '/cash_movement_reasons', params: movementType ? { movement_type: movementType } : {} }),
      keepUnusedDataFor: 300,
    }),

    addCashMovement: builder.mutation({
      query: ({ cash_session_id, movement_type, amount, reason, cash_movement_reason_id }) => ({
        url: `/cash_sessions/${cash_session_id}/cash_movements`,
        method: 'POST',
        body: { cash_movement: { movement_type, amount, reason, ...(cash_movement_reason_id ? { cash_movement_reason_id } : {}) } },
      }),
      // Invalidate session so summary refreshes after movement
      invalidatesTags: (result, error, { cash_session_id }) => [
        { type: 'CashSession', id: cash_session_id },
      ],
    }),

  }),
});

export const {
  useOpenSessionMutation,
  useListSessionsQuery,
  useLazyListSessionsQuery,
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useCloseSessionMutation,
  useAddCashMovementMutation,
  useGetCashMovementReasonsQuery,
} = cashSessionApi;
